import type {} from "@mui/x-data-grid/themeAugmentation"
import type {} from "@mui/x-date-pickers/themeAugmentation"

import { CssBaseline, LinearProgress, PaletteMode, ThemeProvider } from "@mui/material"
import { createTheme, responsiveFontSizes } from "@mui/material/styles"
import { LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon"
import { GridEmpty, GridFooter } from "components/common"
import { PropsWithChildren, useEffect } from "react"
import { useSelector } from "react-redux"
import { themeSelector } from "reducers/themeSlice"

const createAppTheme = (mode?: PaletteMode) =>
  createTheme({
    breakpoints: {
      values: {
        lg: 1200,
        md: 900,
        sm: 600,
        xl: 1600,
        xs: 0,
      },
    },
    components: {
      MuiAutocomplete: {
        styleOverrides: {
          inputRoot: ({ ownerState, theme }) =>
            ownerState.size === "small"
              ? {
                  paddingBottom: theme.spacing(1.1875),
                  paddingTop: theme.spacing(1.1875),
                }
              : {},
        },
      },
      MuiButton: {
        defaultProps: {
          color: "primary",
          disableElevation: true,
          size: "medium",
          variant: "outlined",
        },
        styleOverrides: {
          sizeLarge: { fontSize: 18, minHeight: 48, minWidth: 48 },
          sizeMedium: { fontSize: 16, minHeight: 40, minWidth: 40 },
          sizeSmall: { fontSize: 14, minHeight: 32, minWidth: 32 },
        },
      },
      MuiCard: {
        defaultProps: {
          variant: "outlined",
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: ({ theme }) => ({
            padding: theme.spacing(3, 2),
          }),
        },
      },
      MuiCardHeader: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundColor: theme.palette.action.hover,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }),
        },
      },
      MuiChip: {
        defaultProps: {
          variant: "outlined",
        },
        styleOverrides: {
          root: {
            paddingInline: 4,
          },
        },
      },
      MuiContainer: {
        defaultProps: {
          maxWidth: "lg",
        },
      },
      MuiDataGrid: {
        defaultProps: {
          autoHeight: false,
          getRowHeight: () => "auto",
          pageSizeOptions: [10, 20, 50, 100],
          paginationMode: "server",
          rowSelection: false,
          showCellVerticalBorder: false,
          showColumnVerticalBorder: false,
          slots: {
            footer: GridFooter,
            loadingOverlay: LinearProgress,
            noRowsOverlay: GridEmpty,
          },
          sortingMode: "server",
          sortingOrder: ["desc", "asc"],
        },
        styleOverrides: {
          cell: ({ theme }) => ({
            "&:last-child": { borderRightWidth: 0 },
            minHeight: `${theme.spacing(6)} !important`,
            padding: `${theme.spacing(1, 2)} !important`,
          }),
          columnHeader: ({ theme }) => ({
            "&:last-child": { borderRightWidth: 0 },
            minHeight: `${theme.spacing(6)} !important`,
            padding: `${theme.spacing(1, 2)} !important`,
          }),
          columnHeaders: ({ theme }) => ({
            backgroundColor: theme.palette.action.hover,
          }),
          columnHeaderTitle: ({ theme }) => ({
            fontWeight: theme.typography.fontWeightBold,
          }),
          iconButtonContainer: {
            marginLeft: "auto",
          },
          root: {
            "& *": { outline: "none !important" },
          },
        },
      },
      MuiDayCalendar: {
        styleOverrides: {
          weekDayLabel: ({ theme }) => ({
            ...theme.typography.body2,
            color: theme.palette.text.primary,
            fontWeight: theme.typography.fontWeightBold,
          }),
        },
      },
      MuiDialog: {
        defaultProps: {
          fullWidth: true,
          maxWidth: "sm",
        },
        styleOverrides: {
          paper: ({ theme }) => ({
            borderRadius: theme.shape.borderRadius * 2,
          }),
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderTop: `1px solid ${theme.palette.divider}`,
            gap: theme.spacing(0.5),
            padding: theme.spacing(2, 3),
          }),
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: ({ theme }) => ({
            minHeight: theme.spacing(15),
            paddingTop: `${theme.spacing(3)} !important`,
          }),
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: ({ theme }) => ({
            ...theme.typography.h6,
            borderBottom: `1px solid ${theme.palette.divider}`,
            padding: theme.spacing(2.5, 3),
          }),
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          asterisk: ({ theme }) => ({
            color: theme.palette.error.main,
          }),
          root: ({ theme }) => ({
            color: theme.palette.text.primary,
            fontWeight: theme.typography.fontWeightMedium,
          }),
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: ({ theme }) => ({
            fontSize: theme.typography.body1.fontSize,
          }),
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          input: ({ ownerState, theme }) =>
            ownerState.size === "small"
              ? {
                  paddingBottom: theme.spacing(1.5),
                  paddingTop: theme.spacing(1.5),
                }
              : {},
        },
      },
      MuiPagination: {
        defaultProps: {
          shape: "rounded",
          variant: "outlined",
        },
      },
      MuiPickersDay: {
        styleOverrides: {
          root: ({ theme }) => ({
            ...theme.typography.body2,
            fontWeight: theme.typography.fontWeightMedium,
          }),
        },
      },
      MuiStepConnector: {
        styleOverrides: {
          line: ({ theme }) => ({
            borderColor: theme.palette.primary.main,
          }),
        },
      },
      MuiStepLabel: {
        styleOverrides: {
          label: ({ theme }) => ({
            fontWeight: `${theme.typography.fontWeightBold} !important`,
          }),
        },
      },
      MuiTableCell: {
        styleOverrides: {
          body: ({ theme }) => ({
            padding: theme.spacing(1.5),
          }),
        },
      },
      MuiTextField: {
        defaultProps: {
          InputLabelProps: { shrink: true },
          size: "medium",
          variant: "outlined",
        },
      },
      MuiTooltip: {
        defaultProps: {
          arrow: true,
        },
      },
      MuiTypography: {
        defaultProps: {
          component: "div",
        },
      },
    },
    palette: {
      divider: mode === "dark" ? "#2e2e2e" : "#e0e0e0",
      mode,
      primary: {
        main: "#5f168a",
      },
      secondary: {
        main: "#5865f2",
      },
    },
    shape: {
      borderRadius: 4,
    },
    typography: {
      button: { fontWeight: 700, textTransform: "none" },
      caption: { fontSize: 14, fontWeight: 400, letterSpacing: "0.01071em", lineHeight: 1.43 },
      fontFamily: "Nunito Sans",
      h1: { fontSize: 48, fontWeight: 700, lineHeight: 1.2 },
      h2: { fontSize: 36, fontWeight: 700, lineHeight: 1.2 },
      h3: { fontSize: 30, fontWeight: 700, lineHeight: 1.2 },
      h4: { fontSize: 24, fontWeight: 700, lineHeight: 1.2 },
      h5: { fontSize: 20, fontWeight: 700, lineHeight: 1.2 },
      h6: { fontSize: 18, fontWeight: 700, lineHeight: 1.2 },
      subtitle1: { fontSize: 16, fontWeight: 700, lineHeight: 1.2 },
      subtitle2: { fontSize: 14, fontWeight: 700, lineHeight: 1.2 },
    },
  })

const AppTheme = ({ children }: PropsWithChildren) => {
  const { mode } = useSelector(themeSelector)

  useEffect(() => {
    document.body.dataset.theme = mode
    document.body.className = mode
  }, [mode])

  return (
    <ThemeProvider theme={responsiveFontSizes(createAppTheme(mode))}>
      <LocalizationProvider dateAdapter={AdapterLuxon}>
        <CssBaseline />
        {children}
      </LocalizationProvider>
    </ThemeProvider>
  )
}

export default AppTheme
